import LeanMark from '@/components/ui/LeanMark'
import { selectionCoverage } from '@/components/ui/SelectionLabel'

// The one row layout for the site's lists: Today's stories, the storylines
// leaderboard and a storyline's chapters. Same slots, same places, so a
// reader learns the row once (settled 2026-09-26 after an audit found the
// three lists putting the same facts, count and time and rank, in three
// different places):
//
//   lead    rank (ranked lists) or date (lists in date order); small, grey,
//           on the title's first line
//   body    serif title, then one meta line, then optional context lines
//   visual  the row's one picture, desktop only: lean label + bar for a
//           story or chapter, the coverage sparkline for a storyline
//   figure  the article count, always right-aligned in the same style; in a
//           ranked list it is the number the list is ranked by
//
// Whole-row click: the title link carries ROW_LINK (stretched over the row),
// and any other link inside sits above it with ROW_ABOVE.

// visualKind 'figure' drops the visual column (rows with no picture, like a
// source's storylines); 'none' drops the figure too (rows with no count, like
// a source's own articles). The lead and body stay where they always are.
// The lead sits flush left in a narrow column (2026-09-28): right-aligned in
// a wider one, a single-digit rank floated in from the list's edge while the
// count on the right sat flush, so rows read as padded more on the left.
const COLS = {
  rank: {
    lean: 'grid-cols-[1.5rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[2.5rem_minmax(0,1fr)_200px_5rem]',
    chart:
      'grid-cols-[1.5rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[2.5rem_minmax(0,1fr)_200px_5rem]',
    figure:
      'grid-cols-[1.5rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[2.5rem_minmax(0,1fr)_5rem]',
    none: 'grid-cols-[1.5rem_minmax(0,1fr)] sm:grid-cols-[2.5rem_minmax(0,1fr)]',
  },
  date: {
    lean: 'grid-cols-[3rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[3.5rem_minmax(0,1fr)_200px_5rem]',
    chart:
      'grid-cols-[3rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[3.5rem_minmax(0,1fr)_200px_5rem]',
    figure:
      'grid-cols-[3rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[3.5rem_minmax(0,1fr)_5rem]',
    none: 'grid-cols-[3rem_minmax(0,1fr)] sm:grid-cols-[3.5rem_minmax(0,1fr)]',
  },
}

// The side columns of an overview page (2026-11-01, the concept): two lists
// side by side, so every column is narrower and the title smaller.
const NARROW = {
  lean: 'grid-cols-[1.5rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[1.75rem_minmax(0,1fr)_150px_3.5rem]',
  chart:
    'grid-cols-[1.5rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[1.75rem_minmax(0,1fr)_150px_3.75rem]',
  avatar:
    'grid-cols-[2.25rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[2.5rem_minmax(0,1fr)_150px_3.75rem]',
}

// Mock sizes (2026-11-01): a 22px serif headline on desktop, 18px on a phone.
export const ROW_TITLE =
  'font-serif text-lg font-normal leading-snug text-foreground hover:underline sm:text-[22px] sm:leading-tight'
export const ROW_TITLE_NARROW =
  'font-serif text-lg font-normal leading-snug text-foreground hover:underline'
export const ROW_LINK = "after:absolute after:inset-0 after:content-['']"
export const ROW_ABOVE = 'relative z-10'
export const ROW_META =
  'mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 font-sans text-xs text-muted-foreground'

export default function ListRow({
  lead,
  leadKind = 'rank',
  leadStrong = false,
  visual = null,
  visualKind = 'lean',
  figure,
  unit = 'articles',
  narrow = false,
  wideLead = false,
  children,
}) {
  // A narrow row's lead is a rank or, for a topic, its picture ('avatar');
  // `wideLead` fits both, a rank beside a picture (/topics).
  const cols = wideLead
    ? narrow
      ? 'grid-cols-[3.75rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[4rem_minmax(0,1fr)_150px_3.75rem]'
      : 'grid-cols-[3.75rem_minmax(0,1fr)_3.25rem] sm:grid-cols-[4.5rem_minmax(0,1fr)_200px_5rem]'
    : narrow
      ? NARROW[leadKind === 'avatar' ? 'avatar' : visualKind] || NARROW.lean
      : COLS[leadKind]?.[visualKind] || COLS.rank[visualKind]
  return (
    <li
      className={`relative grid items-start gap-x-3 border-b border-border py-3.5 sm:items-center ${
        narrow ? 'sm:py-3.5' : 'sm:gap-x-4 sm:py-4'
      } ${cols}`}
    >
      <span
        className={`text-left tabular-nums ${
          leadKind === 'rank'
            ? 'pt-0.5 font-serif text-[17px] sm:pt-0 sm:text-xl'
            : leadKind === 'avatar'
              ? 'flex'
              : 'pt-[3px] font-sans text-xs sm:pt-0'
        } ${leadStrong ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
      >
        {lead}
      </span>
      <div className="min-w-0">{children}</div>
      {(visualKind === 'lean' || visualKind === 'chart') && (
        <div className="hidden sm:block">{visual}</div>
      )}
      {visualKind !== 'none' && (
        <span className="text-right">
          <span
            className={`block font-serif text-[19px] leading-none tabular-nums text-foreground ${
              narrow ? 'sm:text-xl' : 'sm:text-[22px]'
            }`}
          >
            {figure}
          </span>
          <span className="mt-0.5 block font-sans text-[11px] leading-tight text-muted-foreground">
            {unit}
          </span>
        </span>
      )}
    </li>
  )
}

// The lean picture for a story or chapter: the row mark (LeanMark, the one
// pattern every list uses: counts at the ends, label under), in the visual
// column on desktop. A phone has no visual column, so LeanInline puts the
// label and a short mark on a line of the meta.
//
// `text` replaces the verdict word where a list wants a figure instead.
export function LeanVisual({ node, text }) {
  return <LeanMark node={node} label={text} />
}

export function LeanInline({ node, text }) {
  const label = text ?? selectionCoverage(node)?.text
  return (
    <span className="flex w-full items-center gap-2.5 sm:hidden">
      {label && (
        <span className="font-sans text-xs font-semibold tabular-nums text-foreground">
          {label}
        </span>
      )}
      <LeanMark node={node} showLabel={false} className="w-32" />
    </span>
  )
}
